import { createRouter, createWebHistory, type RouteRecordRaw } from 'vue-router'
import { useAuthStore } from '../stores/auth'

// Layouts
import AuthLayout from '../layouts/AuthLayout.vue'
import DashboardLayout from '../layouts/DashboardLayout.vue'

// Pages
import Login from '../pages/Login.vue'
import Dashboard from '../pages/Dashboard.vue'
import Users from '../pages/Users.vue'

import UserProfile from '../pages/UserProfile.vue'
import EditUser from '../pages/EditUser.vue'
import Pets from '../pages/Pets.vue'
import AnimalTypes from '../pages/AnimalTypes.vue'
import Breeds from '../pages/Breeds.vue'
import Health from '../pages/Health.vue'
import Education from '../pages/Education.vue'
import Nutrition from '../pages/Nutrition.vue'
import Activities from '../pages/Activities.vue'
import Legal from '../pages/Legal.vue'
import LegalPreview from '../pages/LegalPreview.vue'
import Blocks from '../pages/Blocks.vue'
import Reports from '../pages/Reports.vue'
import ReportDetail from '../pages/ReportDetail.vue'
import ScheduledMessages from '../pages/ScheduledMessages.vue'
import Subscriptions from '../pages/Subscriptions.vue'
import CreatePlan from '../pages/CreatePlan.vue'
import EditPlan from '../pages/EditPlan.vue'
import NotFound from '../pages/NotFound.vue'

const routes: RouteRecordRaw[] = [
  {
    path: '/login',
    component: AuthLayout,
    children: [
      {
        path: '',
        name: 'Login',
        component: Login,
        meta: { requiresGuest: true }
      }
    ]
  },
  {
    path: '/',
    component: DashboardLayout,
    meta: { requiresAuth: true },
    children: [
      {
        path: '',
        name: 'Dashboard',
        component: Dashboard
      },
      {
        path: 'users',
        name: 'Users',
        component: Users,
        meta: { requiresAdmin: true }
      },
      {
        path: 'users/create',
        name: 'UsersCreate',
        component: () => import('../pages/UserCreate.vue'),
        meta: { requiresAdmin: true }
      },

      {
        path: 'user/:id',
        name: 'UserProfile',
        component: UserProfile
      },
      {
        path: 'user/:id/edit',
        name: 'EditUser',
        component: EditUser
      },
      {
        path: 'pets',
        name: 'Pets',
        component: Pets
      },
      {
        path: 'pets/create',
        name: 'PetsCreate',
        component: () => import('../pages/PetForm.vue')
      },
      {
        path: 'pets/:id/edit',
        name: 'PetsEdit',
        component: () => import('../pages/PetForm.vue')
      },
      {
        path: 'animal-types',
        name: 'AnimalTypes',
        component: AnimalTypes,
        meta: { requiresAdmin: true }
      },
      {
        path: 'animal-types/create',
        name: 'AnimalTypesCreate',
        component: () => import('../pages/AnimalTypeForm.vue'),
        meta: { requiresAdmin: true }
      },
      {
        path: 'animal-types/:id/edit',
        name: 'AnimalTypesEdit',
        component: () => import('../pages/AnimalTypeForm.vue'),
        meta: { requiresAdmin: true }
      },
      {
        path: 'breeds',
        name: 'Breeds',
        component: Breeds,
        meta: { requiresAdmin: true }
      },
      {
        path: 'breeds/create',
        name: 'BreedsCreate',
        component: () => import('../pages/BreedForm.vue'),
        meta: { requiresAdmin: true }
      },
      {
        path: 'breeds/:id/edit',
        name: 'BreedsEdit',
        component: () => import('../pages/BreedForm.vue'),
        meta: { requiresAdmin: true }
      },
      {
        path: 'health',
        name: 'Health',
        component: Health,
        meta: { requiresAdmin: true }
      },
      {
        path: 'health/create',
        name: 'HealthCreate',
        component: () => import('../pages/HealthForm.vue'),
        meta: { requiresAdmin: true }
      },
      {
        path: 'health/:id/edit',
        name: 'HealthEdit',
        component: () => import('../pages/HealthForm.vue'),
        meta: { requiresAdmin: true }
      },
      {
        path: 'education',
        name: 'Education',
        component: Education,
        meta: { requiresAdmin: true }
      },
      {
        path: 'education/create',
        name: 'EducationCreate',
        component: () => import('../pages/EducationForm.vue'),
        meta: { requiresAdmin: true }
      },
      {
        path: 'education/:id/edit',
        name: 'EducationEdit',
        component: () => import('../pages/EducationForm.vue'),
        meta: { requiresAdmin: true }
      },
      {
        path: 'nutrition',
        name: 'Nutrition',
        component: Nutrition,
        meta: { requiresAdmin: true }
      },
      {
        path: 'nutrition/create',
        name: 'NutritionCreate',
        component: () => import('../pages/NutritionForm.vue'),
        meta: { requiresAdmin: true }
      },
      {
        path: 'nutrition/:id/edit',
        name: 'NutritionEdit',
        component: () => import('../pages/NutritionForm.vue'),
        meta: { requiresAdmin: true }
      },
      {
        path: 'activities',
        name: 'Activities',
        component: Activities,
        meta: { requiresAdmin: true }
      },
      {
        path: 'legal',
        name: 'Legal',
        component: Legal,
        meta: { requiresAdmin: true }
      },
      {
        path: 'legal/create',
        name: 'LegalCreate',
        component: () => import('../pages/LegalCreate.vue'),
        meta: { requiresAdmin: true }
      },
      {
        path: 'blocks',
        name: 'Blocks',
        component: Blocks,
        meta: { requiresAdmin: true }
      },
      {
        path: 'reports',
        name: 'Reports',
        component: Reports,
        meta: { requiresAdmin: true }
      },
      {
        path: 'reports/:type/:id',
        name: 'ReportDetail',
        component: ReportDetail,
        meta: { requiresAdmin: true }
      },
      {
        path: 'scheduled-messages',
        name: 'ScheduledMessages',
        component: ScheduledMessages,
        meta: { requiresAdmin: true }
      },
      {
        path: 'scheduled-messages/create',
        name: 'ScheduledMessagesCreate',
        component: () => import('../pages/ScheduledMessageCreate.vue'),
        meta: { requiresAdmin: true }
      },
      {
        path: 'subscriptions',
        name: 'Subscriptions',
        component: Subscriptions,
        meta: { requiresAdmin: true }
      },
      {
        path: 'subscriptions/create',
        name: 'CreatePlan',
        component: CreatePlan,
        meta: { requiresAdmin: true }
      },
      {
        path: 'subscriptions/:id/edit',
        name: 'EditPlan',
        component: EditPlan,
        meta: { requiresAdmin: true }
      }
    ]
  },
  {
    path: '/legal-preview/:type',
    name: 'LegalPreview',
    component: LegalPreview,
    meta: { requiresAuth: false }
  },
  {
    path: '/:pathMatch(.*)*',
    name: 'NotFound',
    component: NotFound
  }
]

const router = createRouter({
  history: createWebHistory(),
  routes
})

// Guards de navegación
router.beforeEach((to, _from, next) => {
  const authStore = useAuthStore()
  
  // Inicializar autenticación si no está inicializada
  if (!authStore.user && authStore.token) {
    authStore.initializeAuth()
  }

  // Verificar si la ruta requiere autenticación
  if (to.meta.requiresAuth && !authStore.isAuthenticated) {
    next('/login')
    return
  }

  // Verificar si la ruta requiere ser invitado (no autenticado)
  if (to.meta.requiresGuest && authStore.isAuthenticated) {
    next('/')
    return
  }

  // Verificar si la ruta requiere permisos de admin
  if (to.meta.requiresAdmin && !authStore.isAdmin) {
    next('/')
    return
  }

  next()
})

export default router